import React, { memo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormSection, touch, getFormValues } from "redux-form";

import {
	PROCESSOR_SPONSOR_SECTION_NAME,
	RESIDUAL_SECTION_NAME,
} from "globalConstants";
import { appStatuses } from "mappers";
import { useGetScheduleAListMutation, usePrimaryAgentsQuery } from "queries";
import { uiActions } from "store";
import { DealStage } from "types";

import {
	ProcessorSponsorSection,
	ResidualsFields,
	PrimaryAgentsSection,
	MccCodeSection,
	AchProcessorDdl,
} from "../../formSections";

import styles from "./getStarted.module.scss";

const GetStartedFields = ({
	initialValues,
	formName,
	applicationId,
	isPaperApplication,
	applicationStatus,
	headquartersValue,
	processorSponsorSectionValues,
	loading, // App (for example app edit page) being fetched
	setPDFTemplateType,
	applicationIdEntry,
}) => {
	const dispatch = useDispatch();
	const formValues = useSelector(state => getFormValues(formName)(state));

	const { data: primaryAgents, isLoading: primaryAgentLoading } =
		usePrimaryAgentsQuery({
			onSuccess(response) {
				if (response.length === 0) {
					dispatch(touch(formName, "primaryAgentId"));
					dispatch(
						uiActions.showErrorNotification({
							title: "Your Agent Office doesn't have any active agents",
						}),
					);
				}
			},
		});

	// rawScheduleAs items need to prevent double request on change processor
	const [rawScheduleAs, setRawScheduleAs] = useState([]);
	const [filteredScheduleAs, setFilteredScheduleAs] = useState([]);

	const {
		mutateAsync: handleGetScheduleAList,
		isLoading: isLoadingScheduleAList,
	} = useGetScheduleAListMutation();

	const isUnderwriting = [
		appStatuses.UNDERWRITING_UNASSIGNED,
		appStatuses.UNDERWRITING_IN_PROGRESS,
		appStatuses.UNDERWRITING_REJECTED,
	].includes(applicationStatus);

	const dealStage = isUnderwriting
		? DealStage.Underwriting.name
		: isPaperApplication
		? DealStage.MPAUpload.name
		: DealStage.Application.name;

	return (
		<>
			<PrimaryAgentsSection
				formName={formName}
				applicationId={applicationId}
				primaryAgents={primaryAgents}
				headquartersValue={headquartersValue}
				headquartersLoading={loading}
				primaryAgentLoading={primaryAgentLoading}
				formValues={formValues}
				handleGetScheduleAList={handleGetScheduleAList}
				setRawScheduleAs={setRawScheduleAs}
				setFilteredScheduleAs={setFilteredScheduleAs}
				initialValues={initialValues}
			/>
			<hr className={styles.line} />
			<div className={styles.addressTitle}>
				<h3>Choose Your Processor and Sponsor Bank</h3>
			</div>
			<FormSection name={PROCESSOR_SPONSOR_SECTION_NAME}>
				<ProcessorSponsorSection
					setPDFTemplateType={setPDFTemplateType}
					isResetResidualsValuesOnProcessorChange
					sectionValues={processorSponsorSectionValues}
					dealStage={dealStage}
					applicationId={applicationId}
					formName={formName}
					applicationStatus={applicationStatus}
					isApplication
					applicationIdEntry={applicationIdEntry}
					rawScheduleAs={rawScheduleAs}
					setFilteredScheduleAs={setFilteredScheduleAs}
					formValues={formValues}
				/>
				<AchProcessorDdl />
			</FormSection>

			<hr className={styles.line} />

			<FormSection name="mccCodeSection">
				<MccCodeSection
					formName={formName}
					industryTypeName="whatDoYouSell"
					mccCode={formValues?.mccCodeSection?.mccCode}
					mccAliasId={formValues?.mccCodeSection?.mccAliasId}
					industryType={formValues?.mccCodeSection?.whatDoYouSell}
					isPaperApplication={isPaperApplication}
				/>
			</FormSection>

			<hr className={styles.line} />
			<FormSection name={RESIDUAL_SECTION_NAME}>
				<ResidualsFields
					formName={formName}
					sectionName={RESIDUAL_SECTION_NAME}
					primaryAgents={primaryAgents}
					applicationStatus={applicationStatus}
					isUnderwriting={isUnderwriting}
					scheduleAList={filteredScheduleAs}
					isLoadingScheduleAList={isLoadingScheduleAList}
					initialValues={initialValues}
					handleGetScheduleAList={handleGetScheduleAList}
					setFilteredScheduleAs={setFilteredScheduleAs}
					setRawScheduleAs={setRawScheduleAs}
				/>
			</FormSection>
		</>
	);
};

const component = memo(GetStartedFields);
export { component as GetStartedFields };
